/**
 * WI Hero Video Engine — Ultra-Fast Transitions & Zero-Crash Architecture
 * Precision Web Animations API with Apple-standard deceleration and seamless media layering.
 */
(() => {
  'use strict';

  if (window.__WIHeroVideoLoaded) return;
  window.__WIHeroVideoLoaded = true;

  const ROOT_SELECTOR = '.wi-hero-video[data-wi-hero-root]';
  const states = new WeakMap();
  const tabletMediaQuery = window.matchMedia('(max-width: 989px)');
  const phoneMediaQuery = window.matchMedia('(max-width: 749px)');
  const finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const TOUCH_GESTURE = Object.freeze({
    axisLockDistance: 12,
    axisDominance: 1.2
  });

  const normalizeIndex = (index, length) => (Number(index) + length) % length;

  function getVideoSource(video) {
    if (!video) return '';
    const desktopSource = video.dataset.desktopSrc || '';
    const mobileSource = video.dataset.mobileSrc || '';
    const root = video.closest(ROOT_SELECTOR);
    const mobileMediaMax = Number(root?.dataset.mobileMediaMax) || 749;
    const useMobileMedia =
      mobileMediaMax >= 989 ? tabletMediaQuery.matches : phoneMediaQuery.matches;

    return useMobileMedia ? mobileSource || desktopSource : desktopSource || mobileSource;
  }

  function normalizeUrl(url) {
    if (!url) return '';
    try {
      return new URL(url, document.baseURI).href;
    } catch {
      return url;
    }
  }

  const playPromises = new WeakMap();

  function safePlay(video) {
    if (!video || reduceMotionQuery.matches) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    try {
      const promise = video.play();
      if (promise !== undefined) {
        playPromises.set(video, promise);
        promise
          .catch((err) => {
            if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
              console.warn('Hero video playback notice:', err.name);
            }
          })
          .finally(() => {
            playPromises.delete(video);
          });
      }
    } catch {}
  }

  function safePause(video) {
    if (!video) return;
    const pending = playPromises.get(video);
    if (pending) {
      pending
        .then(() => {
          try {
            video.pause();
          } catch {}
        })
        .catch(() => {});
    } else {
      try {
        video.pause();
      } catch {}
    }
  }

  function markVideoPlaying(video) {
    const slide = video?.closest('[data-wi-hero-slide]');
    if (!slide) return;
    slide.classList.add('is-video-playing');
  }

  function bindVideoEvents(video) {
    if (!video || video.dataset.eventsBound === 'true') return;
    video.dataset.eventsBound = 'true';

    video.addEventListener('playing', () => markVideoPlaying(video), { passive: true });
    video.addEventListener('ended', () => safePlay(video), { passive: true });

    if (!video.paused && video.currentTime > 0) {
      markVideoPlaying(video);
    }
  }

  function prepareVideo(video, { play = false, preload = 'metadata' } = {}) {
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;

    const source = getVideoSource(video);
    if (!source) return;

    bindVideoEvents(video);

    const currentSrc = normalizeUrl(video.currentSrc);
    const requestedSrc = normalizeUrl(source);

    if (video.preload !== preload) {
      video.preload = preload;
    }

    if (video.dataset.currentSrc !== source && currentSrc !== requestedSrc) {
      video.dataset.currentSrc = source;
      video.src = source;
    }

    if (play) {
      safePlay(video);
    }
  }

  function prepareSlide(slide, { play = false, preload = 'metadata' } = {}) {
    if (!slide) return;
    const video = slide.querySelector('[data-wi-hero-slide-video]');
    if (video) {
      prepareVideo(video, { play, preload });
    }
  }

  function initHero(root) {
    if (!root || states.has(root)) return;

    const slider = root.querySelector('[data-wi-hero-slider]');
    if (!slider) return;

    const slides = Array.from(slider.querySelectorAll('[data-wi-hero-slide]'));
    if (!slides.length) return;

    const dots = Array.from(root.querySelectorAll('[data-wi-hero-dot]'));
    const previousButton = root.querySelector('[data-wi-hero-prev]');
    const nextButton = root.querySelector('[data-wi-hero-next]');
    const statusRegion = root.querySelector('[data-wi-hero-status]');

    // Dynamic duration configured via Liquid schema (snappy 380ms default)
    const transitionDuration = Number(slider.dataset.transitionDuration) || 380;
    const isDesignMode = Boolean(window.Shopify?.designMode);
    const autoplay = slider.dataset.autoplay === 'true' && !isDesignMode;
    const interval = Math.max(Number(slider.dataset.interval) || 6000, 2500);

    const state = {
      currentIndex: Math.max(
        slides.findIndex((s) => s.classList.contains('is-active')),
        0
      ),
      timer: null,
      isHoverPaused: false,
      isFocusPaused: false,
      isPointerInteracting: false,
      isEditorPaused: false,
      isAnimating: false,
      isInView: true,
      isDestroyed: false,
      transitionId: 0,
      intersectionObserver: null,
      cleanup: [],
      animations: new Set()
    };

    const gesture = {
      pointerId: null,
      startX: 0,
      startY: 0,
      axis: null,
      direction: 0
    };

    const addListener = (target, type, handler, options) => {
      if (!target) return;
      target.addEventListener(type, handler, options);
      state.cleanup.push(() => target.removeEventListener(type, handler, options));
    };

    const clearTimer = () => {
      if (state.timer !== null) {
        window.clearTimeout(state.timer);
        state.timer = null;
      }
    };

    const canRun = () =>
      !state.isDestroyed &&
      state.isInView &&
      !document.hidden &&
      !state.isHoverPaused &&
      !state.isFocusPaused &&
      !state.isPointerInteracting &&
      !state.isEditorPaused &&
      !reduceMotionQuery.matches;

    const activeSlide = () => slides[state.currentIndex];

    const pauseInactiveVideos = () => {
      slides.forEach((slide, idx) => {
        if (idx !== state.currentIndex) {
          safePause(slide.querySelector('[data-wi-hero-slide-video]'));
        }
      });
    };

    const updateSlideAccessibility = () => {
      const supportsInert = 'inert' in HTMLElement.prototype;
      slides.forEach((slide, idx) => {
        const isActive = idx === state.currentIndex;
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        if (supportsInert) {
          slide.inert = !isActive;
        }
      });
    };

    const updateDots = () => {
      dots.forEach((dot, idx) => {
        const isActive = idx === state.currentIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    };

    const announceSlide = (idx = state.currentIndex) => {
      if (!statusRegion || isDesignMode) return;
      const slide = slides[idx];
      if (!slide) return;
      const label = slide.getAttribute('aria-label') || `Slide ${idx + 1} of ${slides.length}`;
      statusRegion.textContent = '';
      window.requestAnimationFrame(() => {
        if (!state.isDestroyed) statusRegion.textContent = label;
      });
    };

    const cancelTrackedAnimations = () => {
      state.animations.forEach((anim) => {
        try {
          anim.finish();
        } catch {}
      });
      state.animations.clear();
    };

    const trackAnimation = (anim) => {
      if (!anim) return null;
      state.animations.add(anim);
      anim.finished.catch(() => {}).finally(() => state.animations.delete(anim));
      return anim;
    };

    const clearMotionStyles = (slide) => {
      if (!slide) return;
      slide.classList.remove('is-motion-visible', 'is-motion-current', 'is-motion-target');
      slide.style.removeProperty('transform');
      slide.style.removeProperty('visibility');
      slide.style.removeProperty('z-index');
      slide.style.removeProperty('opacity');
    };

    // Buffer the immediate next slide so media starts instantly on transition
    const preloadUpcomingSlide = () => {
      if (slides.length < 2) return;
      const nextSlide = slides[normalizeIndex(state.currentIndex + 1, slides.length)];
      prepareSlide(nextSlide, { play: false, preload: 'auto' });
    };

    const scheduleNext = () => {
      clearTimer();
      if (!autoplay || slides.length < 2 || !canRun()) return;
      state.timer = window.setTimeout(() => {
        goTo(state.currentIndex + 1, { direction: 1 });
      }, interval);
    };

    const resumeHero = () => {
      if (state.isDestroyed || !canRun()) {
        clearTimer();
        return;
      }
      pauseInactiveVideos();
      prepareSlide(activeSlide(), { play: true, preload: 'auto' });
      preloadUpcomingSlide();
      scheduleNext();
    };

    const finishTransition = (prevSlide, nextSlide, nextIdx, { announce = false } = {}) => {
      if (state.isDestroyed) return;

      prevSlide.classList.remove('is-active');
      nextSlide.classList.add('is-active');

      state.currentIndex = nextIdx;
      state.isAnimating = false;
      root.classList.remove('is-animating');

      clearMotionStyles(prevSlide);
      clearMotionStyles(nextSlide);
      cancelTrackedAnimations();

      updateSlideAccessibility();
      updateDots();
      pauseInactiveVideos();

      preloadUpcomingSlide();
      scheduleNext();
      if (announce) announceSlide(nextIdx);
    };

    async function animateProgrammaticTransition(nextIdx, direction, options = {}) {
      if (state.isDestroyed) return;

      const prevSlide = slides[state.currentIndex];
      const nextSlide = slides[nextIdx];
      const transitionId = ++state.transitionId;

      state.isAnimating = true;
      clearTimer();
      cancelTrackedAnimations();
      root.classList.add('is-animating');

      // Video playback commences in parallel with transition execution
      prepareSlide(nextSlide, { play: true, preload: 'auto' });

      nextSlide.classList.add('is-motion-visible', 'is-motion-target');
      prevSlide.classList.add('is-motion-current');

      nextSlide.style.visibility = 'visible';
      nextSlide.style.opacity = '1';
      nextSlide.style.zIndex = '3';
      prevSlide.style.zIndex = '2';

      // Apple-standard responsive deceleration curve
      const easing = 'cubic-bezier(0.16, 1, 0.3, 1)';
      const duration = transitionDuration;

      const anims = [
        trackAnimation(
          prevSlide.animate(
            [
              { transform: 'translate3d(0, 0, 0)' },
              { transform: `translate3d(${-direction * 100}%, 0, 0)` }
            ],
            { duration, easing, fill: 'forwards' }
          )
        ),
        trackAnimation(
          nextSlide.animate(
            [
              { transform: `translate3d(${direction * 100}%, 0, 0)` },
              { transform: 'translate3d(0, 0, 0)' }
            ],
            { duration, easing, fill: 'forwards' }
          )
        )
      ];

      try {
        await Promise.all(anims.map((a) => a?.finished?.catch(() => {}) || Promise.resolve()));
      } finally {
        if (transitionId === state.transitionId && !state.isDestroyed) {
          finishTransition(prevSlide, nextSlide, nextIdx, { announce: options.announce });
        } else {
          state.isAnimating = false;
          root.classList.remove('is-animating');
        }
      }
    }

    function goTo(index, options = {}) {
      if (state.isDestroyed) return;

      const nextIdx = normalizeIndex(index, slides.length);
      if (nextIdx === state.currentIndex) {
        resumeHero();
        return;
      }

      // Fast-forward existing transition immediately to prevent input queuing or stalls
      if (state.isAnimating) {
        cancelTrackedAnimations();
        slides.forEach(clearMotionStyles);
        state.isAnimating = false;
      }

      const direction = options.direction || (nextIdx > state.currentIndex ? 1 : -1);

      if (reduceMotionQuery.matches || typeof Element.prototype.animate !== 'function') {
        finishTransition(slides[state.currentIndex], slides[nextIdx], nextIdx, options);
        prepareSlide(slides[nextIdx], { play: true, preload: 'auto' });
        return;
      }

      animateProgrammaticTransition(nextIdx, direction, options);
    }

    // Touch & Swipe gestures
    if (slides.length > 1) {
      addListener(slider, 'pointerdown', (e) => {
        if (!e.isPrimary || e.pointerType === 'mouse') return;
        gesture.pointerId = e.pointerId;
        gesture.startX = e.clientX;
        gesture.startY = e.clientY;
        gesture.axis = null;
        state.isPointerInteracting = true;
        clearTimer();
      });

      addListener(slider, 'pointermove', (e) => {
        if (e.pointerId !== gesture.pointerId) return;
        const diffX = e.clientX - gesture.startX;
        const diffY = e.clientY - gesture.startY;

        if (!gesture.axis && Math.max(Math.abs(diffX), Math.abs(diffY)) >= TOUCH_GESTURE.axisLockDistance) {
          gesture.axis = Math.abs(diffX) > Math.abs(diffY) * TOUCH_GESTURE.axisDominance ? 'horizontal' : 'vertical';
          if (gesture.axis === 'horizontal') {
            try {
              slider.setPointerCapture(e.pointerId);
            } catch {}
          }
        }
        if (gesture.axis === 'horizontal') {
          gesture.direction = diffX < 0 ? 1 : -1;
        }
      }, { passive: true });

      const finishGesture = (e) => {
        if (e.pointerId !== gesture.pointerId) return;
        const diffX = e.clientX - gesture.startX;
        state.isPointerInteracting = false;
        gesture.pointerId = null;

        if (gesture.axis === 'horizontal' && Math.abs(diffX) > 28) {
          goTo(state.currentIndex + gesture.direction, { direction: gesture.direction, announce: true });
        } else {
          resumeHero();
        }
      };

      addListener(slider, 'pointerup', finishGesture);
      addListener(slider, 'pointercancel', finishGesture);
    }

    dots.forEach((dot) => {
      addListener(dot, 'click', () => {
        const idx = Number(dot.dataset.slideIndex);
        if (!Number.isNaN(idx)) goTo(idx, { announce: true });
      });
    });

    if (previousButton) {
      addListener(previousButton, 'click', () => goTo(state.currentIndex - 1, { direction: -1, announce: true }));
    }
    if (nextButton) {
      addListener(nextButton, 'click', () => goTo(state.currentIndex + 1, { direction: 1, announce: true }));
    }

    if (autoplay && slides.length > 1) {
      addListener(root, 'focusin', () => { state.isFocusPaused = true; clearTimer(); });
      addListener(root, 'focusout', (e) => {
        if (e.relatedTarget && root.contains(e.relatedTarget)) return;
        state.isFocusPaused = false;
        resumeHero();
      });
    }

    if (finePointerQuery.matches && slides.length > 1) {
      addListener(root, 'mouseenter', () => { state.isHoverPaused = true; clearTimer(); });
      addListener(root, 'mouseleave', () => { state.isHoverPaused = false; resumeHero(); });
    }

    addListener(document, 'visibilitychange', () => {
      if (document.hidden) {
        clearTimer();
        pauseInactiveVideos();
        safePause(activeSlide()?.querySelector('[data-wi-hero-slide-video]'));
      } else {
        resumeHero();
      }
    });

    if ('IntersectionObserver' in window) {
      state.intersectionObserver = new IntersectionObserver((entries) => {
        state.isInView = Boolean(entries[0]?.isIntersecting);
        if (state.isInView) resumeHero();
        else pauseInactiveVideos();
      }, { rootMargin: '200px 0px', threshold: 0.01 });

      state.intersectionObserver.observe(root);
    }

    // Launch first slide video and warm up the next upcoming slide
    prepareSlide(slides[state.currentIndex], { play: true, preload: 'auto' });
    preloadUpcomingSlide();

    updateSlideAccessibility();
    updateDots();

    state.goTo = goTo;
    state.pauseAutoplay = () => {
      state.isEditorPaused = true;
      clearTimer();
    };
    state.resumeAutoplay = () => {
      state.isEditorPaused = false;
      resumeHero();
    };
    state.destroy = () => {
      state.isDestroyed = true;
      clearTimer();
      cancelTrackedAnimations();
      slides.forEach((s) => safePause(s.querySelector('[data-wi-hero-slide-video]')));
      state.intersectionObserver?.disconnect();
      state.cleanup.forEach((c) => c());
      states.delete(root);
    };

    states.set(root, state);
  }

  function initAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) initHero(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initHero);
  }

  initAll();

  // Theme Editor Lifecycle Integration
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target.matches?.(ROOT_SELECTOR) ? e.target : e.target.querySelector?.(ROOT_SELECTOR);
    if (root) states.get(root)?.destroy?.();
  });
  document.addEventListener('shopify:section:select', (e) => {
    const root = e.target.matches?.(ROOT_SELECTOR) ? e.target : e.target.querySelector?.(ROOT_SELECTOR);
    states.get(root)?.pauseAutoplay?.();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const root = e.target.matches?.(ROOT_SELECTOR) ? e.target : e.target.querySelector?.(ROOT_SELECTOR);
    states.get(root)?.resumeAutoplay?.();
  });
  document.addEventListener('shopify:block:select', (e) => {
    const blockId = e.detail?.blockId;
    if (!blockId) return;
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const state = states.get(root);
      const slide = root.querySelector(`[data-block-id="${blockId}"]`);
      if (state && slide) {
        const idx = Number(slide.dataset.slideIndex);
        if (!Number.isNaN(idx)) {
          state.pauseAutoplay();
          state.goTo(idx);
        }
      }
    });
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const root = e.target.matches?.(ROOT_SELECTOR) ? e.target : e.target.querySelector?.(ROOT_SELECTOR);
    states.get(root)?.resumeAutoplay?.();
  });
})();